<template>
  <!-- Collapsed state - Small indicator in top right -->
  <div
    v-if="isImpersonating && isCollapsed"
    @click="isCollapsed = false"
    class="impersonation-banner-collapsed"
    data-test-id="impersonation-banner-collapsed"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
    <span>Impersonation</span>
  </div>

  <!-- Expanded state - Full banner -->
  <div v-if="isImpersonating && !isCollapsed" class="impersonation-banner">
    <div class="impersonation-banner__content" data-test-id="impersonation-banner">
      <div class="impersonation-banner__info">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <div class="impersonation-banner__text">
          <strong>{{ t('impersonation.banner.title') }}</strong>
          <span>{{ t('impersonation.banner.description', { email: targetEmail || '...' }) }}</span>
        </div>
      </div>
      <div class="impersonation-banner__actions">
        <button
          data-test-id="impersonation-back-account"
          @click="handleStopImpersonation"
          :disabled="isStopping"
          class="impersonation-banner__button"
        >
          {{ isStopping ? t('impersonation.banner.stopping') : t('impersonation.banner.stopButton') }}
        </button>
        <button
          @click="isCollapsed = true"
          class="impersonation-banner__collapse"
          :title="t('impersonation.banner.collapse')"
          data-test-id="close-impersonation-banner"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="18 15 12 9 6 15"></polyline>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useImpersonation } from '@/features/admin/users/hooks/use-impersonation.hook'
import { useI18n } from '@/lib/i18n'
import useAccount from '@/features/auth/hooks/use-account.hook'

const { t } = useI18n()
const { isImpersonating, impersonationStatus, stopImpersonation, isStopping } = useImpersonation()

const { accountRef } = useAccount()

// Collapsed state - start collapsed to not obstruct the navbar
const isCollapsed = ref(true)

// Get target email from API status or from current account
const targetEmail = computed(() => {
  return impersonationStatus.value?.targetUser?.email || accountRef.value?.email
})

function handleStopImpersonation() {
  if (confirm(t('impersonation.banner.confirmStop'))) {
    stopImpersonation()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/utils';

.impersonation-banner-collapsed {
  position: fixed;
  top: $s * 2;
  left: $s * 2;
  z-index: z('impersonation-banner');
  display: flex;
  align-items: center;
  gap: $s;
  padding: $s $s * 2;
  background: linear-gradient(135deg, #c2410c 0%, #9a3412 100%);
  color: white;
  border-radius: $s * 3;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    cursor: pointer;
  }

  svg {
    flex-shrink: 0;
  }
}

.impersonation-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: z('impersonation-banner');
  background: linear-gradient(135deg, #c2410c 0%, #9a3412 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: slideDown 0.3s ease;

  &__content {
    max-width: 1400px;
    margin: 0 auto;
    padding: $s * 2 $s * 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $s * 2;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: $s * 2;
    flex: 1;
    min-width: 0;

    svg {
      flex-shrink: 0;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: $s / 2;
    min-width: 0;

    strong {
      font-size: 16px;
      font-weight: 700;
    }

    span {
      font-size: 14px;
      opacity: 0.95;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $s;
    flex-shrink: 0;
  }

  &__button {
    padding: $s $s * 2;
    background-color: white;
    color: #9a3412;
    border: none;
    border-radius: $s / 2;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background-color: rgba(white, 0.9);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__collapse {
    padding: $s;
    background-color: transparent;
    border: none;
    color: white;
    cursor: pointer;
    border-radius: $s / 2;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(white, 0.15);
    }

    svg {
      display: block;
    }
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
